import {useEffect, useLayoutEffect, useRef, useState} from 'react'

/**
 * # Flyout motion sequencing
 *
 * A level change is a *sequence*: the content on screen animates away, the
 * panel resizes to fit what is coming, then the new content animates in. CSS
 * has no notion of "then" — when React flips state every rule re-resolves in
 * the same frame — so a sequence written in CSS alone has to be faked with
 * `transition-delay`. Delays are open-loop: they do not know how long the step
 * before them actually took, they cannot be interrupted, and a `display` toggle
 * silently re-arms them from the top. That is the whole of the chaos this
 * module exists to remove.
 *
 * So the sequence lives here, in one small machine, and CSS only ever renders
 * the phase it is told. Phases advance on animation *completion*, never on a
 * timer, which means the timing values in the recipes are free to change
 * without anything here needing to agree with them.
 */

/**
 * Phase the flyout content is in. Published to the DOM so the recipes can
 * style each step; see `flyout-navigation.recipe.js`.
 *
 * - `idle` — resting, and no level has changed yet.
 * - `exiting` — the outgoing content is animating away.
 * - `resizing` — content is swapped and invisible; the panel is changing size.
 * - `entering` — the incoming content is animating in.
 * - `settled` — resting again, after a level change.
 *
 * `idle` and `settled` are both at rest, and the difference between them is the
 * reason there are two: the recipes hold the incoming animation named through
 * `settled`, because renaming it is what would restart it. `idle` is the resting
 * state of a flyout that has only been opened, where the recipes want the plain
 * open cascade instead. See `LEVEL_MOTION` in `flyout-stagger.js`.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const PHASE = Object.freeze({
    IDLE: 'idle',
    EXITING: 'exiting',
    RESIZING: 'resizing',
    ENTERING: 'entering',
    SETTLED: 'settled'
})

/**
 * `getAnimations` reports CSS transitions alongside CSS animations, and the nav
 * rows carry hover transitions on colour and transform (`navigation-parent`,
 * `navigation-link`, `navigation-teaser`). Waiting on those would stall a level
 * change for as long as the pointer happens to rest on a link — a bug that only
 * shows up when someone moves the mouse, which is to say almost never in test
 * and constantly in use. Only CSS animations drive the sequence; the panel
 * resize is a script animation awaited directly by its owner.
 *
 * @param {Animation} animation
 * @returns {boolean}
 */
const isCssAnimation = (animation) => 'animationName' in animation

/**
 * Everything running on `element` and its subtree that `accept` admits.
 *
 * @param {Element|null|undefined} element
 * @param {(animation: Animation) => boolean} accept
 * @returns {Animation[]|undefined}
 */
const select = (element, accept) => element?.getAnimations?.({subtree: true})?.filter(accept)

/**
 * Resolves once every animation given has finished, been cancelled, or been
 * replaced.
 *
 * @param {Animation[]|undefined} animations
 * @returns {Promise<void>}
 */
const finish = (animations) => {
    if (!animations?.length) return Promise.resolve()
    // `finished` rejects when an animation is cancelled, which is the ordinary
    // outcome of superseding a phase mid-flight — a result here, not a failure.
    return Promise.allSettled(animations.map((animation) => animation.finished)).then(
        () => undefined
    )
}

/**
 * Resolves once the CSS animations running on `element` and its subtree have
 * finished, been cancelled, or been replaced.
 *
 * This is the load-bearing piece: because it observes what the browser is
 * *actually* running rather than predicting how long it ought to take, the
 * stagger can be any length, the recipes can be retimed freely, and neither can
 * desynchronise the sequence.
 *
 * Two behaviours fall out of it for free rather than needing their own branch:
 *
 * - **Reduced motion.** `_motionReduce` collapses every animation to `0.01ms`,
 *   so this resolves on the next tick and the machine runs through its phases
 *   in a few frames. There is no reduced-motion code path to keep in step.
 * - **Tests.** jsdom implements neither `getAnimations` nor the Web Animations
 *   API, so this resolves in the same tick and the sequence completes
 *   synchronously — no `animationend` that never arrives to hang a suite.
 *
 * @param {Element|null|undefined} element - Subtree root to wait on.
 * @returns {Promise<void>}
 */
export const settled = (element) => finish(select(element, isCssAnimation))

/**
 * Resolves once the close has finished: the exit animations anywhere under
 * `element`, and the transitions on `element` itself.
 *
 * `settled` cannot measure the close alone — the canvas collapse is a CSS
 * transition and outlasts the cascade, so a close read from the cascade would
 * cut it mid-travel.
 *
 * Two hazards ride on the narrowness. The mobile panel fade is a *descendant*
 * transition and so goes unobserved; it is covered only because the reveal
 * delays its own row change to wait the fade out (see the recipe), which a
 * retiming of either could break. And subtree animations are taken whole, so an
 * endless one below would never resolve this.
 *
 * @param {Element|null|undefined} element - Element whose transitions mark the
 *   close, and whose subtree carries the exit cascade.
 * @returns {Promise<void>}
 */
export const rested = (element) =>
    finish(
        select(
            element,
            (animation) => isCssAnimation(animation) || animation.effect?.target === element
        )
    )

/**
 * The phases that advance, and what each advances to. A phase absent from here
 * is a resting one: nothing is in flight and there is nothing to wait on.
 *
 * @type {Readonly<Record<string, string>>}
 */
const NEXT_PHASE = Object.freeze({
    [PHASE.EXITING]: PHASE.RESIZING,
    [PHASE.RESIZING]: PHASE.ENTERING,
    [PHASE.ENTERING]: PHASE.SETTLED
})

/**
 * Presents `target` by way of an exit/resize/enter sequence, holding the
 * previously presented value on screen until the exit has finished.
 *
 * ## Interruption
 *
 * The pending target is a variable, never a queue — the single rule that stops
 * fast clicking from compounding into a backlog of animations:
 *
 * - a new target while `exiting` **retargets** the run already under way;
 * - a new target while `entering` starts a fresh `exiting`;
 * - a new target while `resizing` is picked up when the sequence next rests;
 * - `enabled: false` abandons everything and snaps, so closing always wins.
 *
 * @param {*} target - Value to present; compared by identity.
 * @param {Object} options
 * @param {React.RefObject<Element>} options.contentRef - Subtree whose CSS
 *   animations mark the exit and enter phases as complete.
 * @param {() => Promise<void>} [options.onResize] - Awaited between the swap
 *   and the enter, to animate the panel to its new size. Runs after `presented`
 *   has been committed, so it can measure the incoming content. Must be
 *   referentially stable (`useCallback`) or the sequence restarts each render.
 * @param {boolean} [options.enabled] - When false, `target` is presented
 *   immediately with no motion. Used to skip the sequence while the flyout is
 *   shut, where there is nothing to see.
 * @returns {{presented: *, phase: string}}
 */
export const usePhasedSwap = (target, {contentRef, onResize, enabled = true}) => {
    const [presented, setPresented] = useState(target)
    const [phase, setPhase] = useState(PHASE.IDLE)

    // Read at the moment of the swap rather than captured when the sequence
    // started, so a second request retargets the run in flight.
    const targetRef = useRef(target)
    targetRef.current = target

    // Tags each phase advance. A settle that resolves after its run was
    // superseded — or after unmount — is discarded instead of advancing the
    // machine a second time.
    const runRef = useRef(0)

    // `onResize` is called from an async step; holding it in a ref keeps an
    // unstable callback from restarting the sequence mid-flight.
    const resizeRef = useRef(onResize)
    resizeRef.current = onResize

    useEffect(() => {
        if (!enabled) {
            setPresented(target)
            setPhase(PHASE.IDLE)
            return
        }
        if (target === presented) return
        // `exiting` already has the new target via the ref; `resizing` is
        // mid-measure and is left to finish and settle.
        if (phase !== PHASE.EXITING && phase !== PHASE.RESIZING) setPhase(PHASE.EXITING)
    }, [enabled, phase, target, presented])

    useLayoutEffect(() => {
        if (!enabled || !NEXT_PHASE[phase]) return undefined

        const run = ++runRef.current
        const superseded = () => run !== runRef.current

        const advance = async () => {
            if (phase === PHASE.RESIZING) {
                await resizeRef.current?.()
            } else {
                await settled(contentRef.current)
            }
            if (superseded()) return
            // The swap rides along with the phase change so both land in one
            // commit: the content is replaced in the same frame it becomes
            // invisible, never a frame where the old content is already gone.
            if (phase === PHASE.EXITING) setPresented(targetRef.current)
            setPhase(NEXT_PHASE[phase])
        }

        advance()

        // Invalidates the run in flight on any phase change or unmount.
        return () => {
            runRef.current += 1
        }
    }, [enabled, phase, contentRef])

    return {presented, phase}
}
